import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { IG_DM, buildGeneralMessage, waLink } from "../../lib/order";
import { WhatsAppMark } from "./QuickChat";
import { EASE } from "./Reveal";

const sectionLinks = [
  { label: "Collection", id: "collection" },
  { label: "Reviews", id: "reviews" },
  { label: "Contact", id: "contact" },
];

export function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
  else el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const goSection = (id) => {
    setMenuOpen(false);
    if (location.pathname !== "/") navigate("/", { state: { section: id } });
    else scrollToId(id);
  };

  const goRoute = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  const goHome = () => {
    setMenuOpen(false);
    if (location.pathname !== "/") navigate("/");
    else if (window.__lenis) window.__lenis.scrollTo(0);
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goCollection = (cat) => {
    setMenuOpen(false);
    if (location.pathname !== "/") navigate(`/?c=${cat}`, { state: { section: "collection" } });
    else { navigate(`/?c=${cat}`); setTimeout(() => scrollToId("collection"), 250); }
  };

  const menuItems = [
    { label: "Bloom Everyday", action: () => goCollection("bloom"), testid: "mobile-nav-collection-bloom" },
    { label: "Onam Collection", action: () => goCollection("onam"), testid: "mobile-nav-collection-onam" },
    { label: "Everyday Collection", action: () => goCollection("everyday"), testid: "mobile-nav-collection-everyday" },
    { label: "Bloom Catalogue", action: () => goRoute("/catalogue?c=bloom"), testid: "mobile-nav-catalogue-bloom" },
    { label: "Onam Catalogue", action: () => goRoute("/catalogue?c=onam"), testid: "mobile-nav-catalogue-onam" },
    { label: "Everyday Catalogue", action: () => goRoute("/catalogue?c=everyday"), testid: "mobile-nav-catalogue-everyday" },
    { label: "Story", action: () => goRoute("/about"), testid: "mobile-nav-story" },
    { label: "Reviews", action: () => goSection("reviews"), testid: "mobile-nav-reviews" },
    { label: "Contact", action: () => goSection("contact"), testid: "mobile-nav-contact" },
  ];

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: EASE, delay: 0.4 }}
      className="fixed top-[41px] left-0 z-50 w-full"
    >
      <div
        className={`flex items-center justify-between px-6 md:px-12 transition-[padding,background-color,backdrop-filter] duration-500 ${
          scrolled || menuOpen
            ? "py-4 bg-black/75 backdrop-blur-md border-b border-white/10"
            : "py-5 md:py-7 bg-transparent"
        }`}
      >
        <button data-testid="nav-logo" onClick={goHome} className="flex items-center">
          <img src="/keilani-logo-white.png" alt="Keilani Clothing" className="h-4 md:h-5 w-auto" />
        </button>

        {/* desktop nav */}
        <nav className="hidden md:flex items-center gap-7 font-body text-[11px] uppercase tracking-[0.22em] text-white/60">
          <NavDropdown
            testid="nav-collection"
            label="Collection"
            items={[
              { label: "Bloom Everyday", testid: "nav-collection-bloom", onClick: () => {
                setMenuOpen(false);
                if (location.pathname !== "/") navigate("/?c=bloom", { state: { section: "collection" } });
                else { navigate("/?c=bloom"); setTimeout(() => scrollToId("collection"), 250); }
              }},
              { label: "Onam", testid: "nav-collection-onam", onClick: () => {
                setMenuOpen(false);
                if (location.pathname !== "/") navigate("/?c=onam", { state: { section: "collection" } });
                else { navigate("/?c=onam"); setTimeout(() => scrollToId("collection"), 250); }
              }},
              { label: "Everyday", testid: "nav-collection-everyday", onClick: () => {
                setMenuOpen(false);
                if (location.pathname !== "/") navigate("/?c=everyday", { state: { section: "collection" } });
                else { navigate("/?c=everyday"); setTimeout(() => scrollToId("collection"), 250); }
              }},
            ]}
          />
          <NavDropdown
            testid="nav-catalogue"
            label="Catalogue"
            items={[
              { label: "Bloom Everyday", testid: "nav-catalogue-bloom", onClick: () => navigate("/catalogue?c=bloom") },
              { label: "Onam", testid: "nav-catalogue-onam", onClick: () => navigate("/catalogue?c=onam") },
              { label: "Everyday", testid: "nav-catalogue-everyday", onClick: () => navigate("/catalogue?c=everyday") },
            ]}
          />
          <NavBtn testid="nav-story" onClick={() => navigate("/about")} label="Story" />
          {sectionLinks.slice(1).map((l) => (
            <NavBtn key={l.id} testid={`nav-${l.id}`} onClick={() => goSection(l.id)} label={l.label} />
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-2.5">
          {/* WhatsApp — icon-only on mobile, pill on desktop */}
          <a
            data-testid="nav-order-whatsapp"
            href={waLink(buildGeneralMessage())}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Order on WhatsApp"
            className="inline-flex h-10 items-center gap-2 rounded-full bg-[#25D366] px-3 md:px-4 text-white transition-transform duration-300 hover:brightness-110 hover:-translate-y-[1px]"
          >
            <WhatsAppMark className="h-4 w-4" />
            <span className="hidden md:inline font-body text-[11px] uppercase tracking-[0.24em]">
              WhatsApp
            </span>
          </a>

          {/* Instagram DM */}
          <a
            data-testid="nav-order-dm"
            href={IG_DM}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex h-10 items-center font-body text-[11px] uppercase tracking-[0.24em] border border-white/40 rounded-full px-4 text-white transition-colors duration-300 hover:bg-white hover:text-black"
          >
            Instagram DM
          </a>

          {/* mobile hamburger */}
          <button
            data-testid="nav-menu-toggle"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMenuOpen((o) => !o)}
            className="md:hidden flex h-10 w-10 items-center justify-center text-white"
          >
            {menuOpen ? <X className="h-6 w-6" strokeWidth={1.3} /> : <Menu className="h-6 w-6" strokeWidth={1.3} />}
          </button>
        </div>
      </div>

      {/* mobile menu overlay */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3, ease: EASE }}
            className="md:hidden fixed inset-x-0 top-[calc(41px+57px)] bottom-0 z-40 bg-[#050505]/98 backdrop-blur-md"
          >
            <nav className="flex h-full flex-col justify-center px-8 pb-24">
              {menuItems.map((item, i) => (
                <motion.button
                  key={item.label}
                  data-testid={item.testid}
                  onClick={item.action}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i + 0.06, ease: EASE, duration: 0.35 }}
                  className="border-b border-white/10 py-4 text-left font-display text-2xl font-light text-white/90 transition-colors hover:text-[var(--kln-gold)]"
                >
                  {item.label}
                </motion.button>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, ease: EASE, duration: 0.4 }}
                className="mt-10 flex flex-wrap gap-3"
              >
                <a
                  data-testid="mobile-nav-order-whatsapp"
                  href={waLink(buildGeneralMessage())}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMenuOpen(false)}
                  className="inline-flex items-center gap-3 rounded-full bg-[#25D366] px-7 py-4 font-body text-[12px] uppercase tracking-[0.24em] text-white transition-transform duration-300 hover:brightness-110"
                >
                  <WhatsAppMark className="h-4 w-4" />
                  WhatsApp us
                </a>
                <a
                  data-testid="mobile-nav-order"
                  href={IG_DM}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMenuOpen(false)}
                  className="inline-flex items-center gap-3 rounded-full bg-white px-7 py-4 font-body text-[12px] uppercase tracking-[0.24em] text-black"
                >
                  Instagram DM
                  <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                </a>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}

function NavBtn({ label, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className="relative py-1 transition-colors duration-300 hover:text-white group">
      {label}
      <span className="absolute left-0 -bottom-0.5 h-px w-0 bg-white transition-[width] duration-500 group-hover:w-full" />
    </button>
  );
}

function NavDropdown({ label, items, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        data-testid={testid}
        onClick={() => setOpen((o) => !o)}
        className="relative py-1 transition-colors duration-300 hover:text-white group"
      >
        {label}
        <span className="absolute left-0 -bottom-0.5 h-px w-0 bg-white transition-[width] duration-500 group-hover:w-full" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.2 }}
            className="absolute left-1/2 top-full z-50 mt-3 -translate-x-1/2 min-w-[190px] rounded-xl border border-white/10 bg-black/90 backdrop-blur-md p-1.5 shadow-xl"
          >
            {items.map((it) => (
              <button
                key={it.testid}
                data-testid={it.testid}
                onClick={() => {
                  setOpen(false);
                  it.onClick();
                }}
                className="block w-full rounded-lg px-4 py-2.5 text-left font-body text-[11px] uppercase tracking-[0.22em] text-white/70 transition-colors hover:bg-white/10 hover:text-white"
              >
                {it.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
